import type { RootState } from '@/app/store'
import type { AGGridFilterModel } from '@/types/agGridFilters'
import type { PageConfigRequest } from '@/types/api/page-config'
import type { ModelListConfig } from '@/types/schema/page-configs/model-list'

import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit'
import { isEqual } from 'lodash'

import { updatePageConfig } from '@/legacy-api/page-config-slice'
import { defaultPageConfigs } from '@/reducers/defaults/page-configs'
import { Page } from '@/types/schema/page-configs'
import { decodeFilter } from '@/utils/decode-filters'

const MODEL_LIST_SCHEMA_VERSION = '1.0'

interface ModelListState {
  config: ModelListConfig
  configVersion: string
  defaultConfig: ModelListConfig
  searchTerm: string | null
  filters: AGGridFilterModel | null
}

const initialState: ModelListState = {
  config: defaultPageConfigs[Page.ModelList],
  configVersion: MODEL_LIST_SCHEMA_VERSION,
  defaultConfig: defaultPageConfigs[Page.ModelList],
  searchTerm: null,
  filters: null,
}

const modelListSlice = createSlice({
  name: 'modelList',
  reducerPath: 'modelList',
  initialState,
  reducers: {
    initializeModelListConfig: handleInitializeModelListConfig,
    reset: handleReset,
    resetModelListConfig: handleResetModelListConfig,
    resetSearchTerm: handleResetSearchTerm,
    setModelListConfig: handleSetModelListConfig,
    setSearchTerm: handleSetSearchTerm,
    setFilters: handleSetFilters,
  },
})

function handleReset(state: ModelListState) {
  Object.assign(state, initialState)
}

function handleInitializeModelListConfig(
  state: ModelListState,
  action: PayloadAction<{ config: ModelListConfig; version: string }>,
) {
  state.config = action.payload.config
  state.configVersion = action.payload.version
  state.defaultConfig = action.payload.config
}

function handleResetModelListConfig(state: ModelListState) {
  state.config = state.defaultConfig
}

function handleSetModelListConfig(
  state: ModelListState,
  action: PayloadAction<Partial<ModelListConfig>>,
) {
  const updatedConfig = {
    ...state.config,
    ...action.payload,
  }
  state.config = updatedConfig
}

function handleSetSearchTerm(
  state: ModelListState,
  action: PayloadAction<string>,
) {
  state.searchTerm = action.payload
}

function handleResetSearchTerm(state: ModelListState) {
  state.searchTerm = null
}

function handleSetFilters(
  state: ModelListState,
  action: PayloadAction<string>,
) {
  // If the filter is the same, don't update the state
  const payloadJson = action.payload ? decodeFilter(action.payload) : null
  if (isEqual(state.filters, payloadJson)) {
    return
  }
  if (!action.payload || action.payload === '') {
    state.filters = null
    return
  }
  state.filters = JSON.parse(decodeURIComponent(action.payload))
}

export const persistModelListConfigAsyncThunk = createAsyncThunk(
  // biome-ignore lint/style/useTemplate: Bulk suppression as part of biome migration
  modelListSlice.reducerPath + '/persistModelListConfig',
  async (_data, { dispatch, getState, rejectWithValue }) => {
    const state = (getState() as RootState).modelList
    try {
      const request: PageConfigRequest = {
        id: Page.ModelList,
        config: state.config,
        version: state.configVersion,
      }
      await dispatch(updatePageConfig.initiate(request))
    } catch (err) {
      const errorMessage =
        // biome-ignore lint/suspicious/noExplicitAny: Bulk suppression as part of biome migration
        (err as { response?: { data?: any } }).response?.data ||
        'An unknown error occurred'
      return rejectWithValue(errorMessage)
    }
  },
)

export const {
  initializeModelListConfig,

  resetModelListConfig,

  setModelListConfig,
  setSearchTerm,
  setFilters,
} = modelListSlice.actions

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default modelListSlice
